import { Controller } from "@hotwired/stimulus"

// Handles offline payment method selection, fee preview, USSD copy, and bank details display
export default class extends Controller {
  static values = {
    schoolId: String,
    amount: Number,
    manualFeePreviewUrl: String,
  }

  static targets = [
    "bankAccountId",
    "methodCards",
    "feePreview",
    "subtotalDisplay",
    "withdrawalFeeDisplay",
    "totalDisplay",
    "bankDetails",
    "bankDetailsContent",
    "submitBtn",
    "ussdCode",
  ]

  // A method may be chosen before this controller runs — clicked while the
  // page was still loading its scripts, or checked by the server when the
  // school has only one: no « change » will come for it, and the button
  // stayed disabled for good (QA round 4, PAR4-09).
  connect() {
    const checked = this.element.querySelector(
      'input[type="radio"][name="payment_method"]:checked',
    )
    if (checked) this.applyMethod(checked)
  }

  selectMethod(event) {
    this.applyMethod(event.target)
  }

  applyMethod(radio) {
    const bankAccountId = radio.dataset.bankAccountId
    const methodType = radio.dataset.methodType

    // Set bank account ID if present
    if (this.hasBankAccountIdTarget && bankAccountId) {
      this.bankAccountIdTarget.value = bankAccountId
    }

    // Enable submit button
    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.disabled = false
    }

    if (methodType === "bank_transfer") {
      this.showBankDetails(radio)
      this.hideFeePreview()
    } else {
      this.hideBankDetails()
      this.fetchFeePreview(methodType)
    }
  }

  async fetchFeePreview(operator) {
    if (!this.manualFeePreviewUrlValue) return

    try {
      const url = new URL(this.manualFeePreviewUrlValue, window.location.origin)
      url.searchParams.set("school_id", this.schoolIdValue)
      url.searchParams.set("operator", operator)

      const response = await fetch(url.toString(), {
        headers: { "Accept": "application/json" },
      })

      if (!response.ok) return

      const data = await response.json()
      this.displayFeePreview(data)
    } catch (error) {
      console.error("Fee preview error:", error)
    }
  }

  displayFeePreview(data) {
    if (!this.hasFeePreviewTarget) return

    if (!data.withdrawal_fee || data.withdrawal_fee <= 0) {
      this.hideFeePreview()
      return
    }

    this.feePreviewTarget.classList.remove("hidden")

    if (this.hasSubtotalDisplayTarget) {
      this.subtotalDisplayTarget.textContent = this.formatCurrency(data.subtotal)
    }
    if (this.hasWithdrawalFeeDisplayTarget) {
      this.withdrawalFeeDisplayTarget.textContent = this.formatCurrency(data.withdrawal_fee)
    }
    if (this.hasTotalDisplayTarget) {
      this.totalDisplayTarget.textContent = this.formatCurrency(data.total_amount)
    }
  }

  hideFeePreview() {
    if (this.hasFeePreviewTarget) {
      this.feePreviewTarget.classList.add("hidden")
    }
  }

  showBankDetails(radio) {
    if (!this.hasBankDetailsTarget) return

    // Find the selected method's label to extract bank info text
    const label = radio.closest("label")
    const bankInfoText = label?.querySelector(".text-xs")?.textContent || ""

    if (this.hasBankDetailsContentTarget) {
      this.bankDetailsContentTarget.innerHTML = `<p class="text-sm text-gray-700">${bankInfoText}</p>`
    }

    this.bankDetailsTarget.classList.remove("hidden")
  }

  hideBankDetails() {
    if (this.hasBankDetailsTarget) {
      this.bankDetailsTarget.classList.add("hidden")
    }
  }

  copyUssd() {
    if (!this.hasUssdCodeTarget) return

    const code = this.ussdCodeTarget.textContent.trim()
    navigator.clipboard.writeText(code).then(() => {
      // Brief visual feedback
      const btn = event.currentTarget
      const originalText = btn.textContent
      btn.textContent = "✓"
      setTimeout(() => { btn.textContent = originalText }, 1500)
    }).catch(() => {
      // Fallback for older browsers
      const textArea = document.createElement("textarea")
      textArea.value = code
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand("copy")
      document.body.removeChild(textArea)
    })
  }

  formatCurrency(amount) {
    return `${Math.round(amount).toLocaleString("fr-FR")} F CFA`
  }
}
